import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import SectionContext from '../../../../contexts/SectionContext'
import UserContext from '../../../../contexts/UserContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import { Eye, Pencil, Trash2 } from 'lucide-react'
import { CollectionsOutletType } from '../index'
import ViewCollectionModal from '../ViewCollectionModal'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { CollectionsTableContainer, InfoMessage } from './style'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import { buildCollectionColumns, collectionsScreenKey, CollectionRow } from './collectionColumns'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

function sanitizeCode(rawCode: any): string {
  if (rawCode == null) return ''
  const str = String(rawCode).trim()
  if (!str) return ''
  return str.replace(/\s*-\s*/g, '-')
}

const CollectionsTable: React.FC = () => {
  const { refresh, search, setIsLoading, appliedFilters } = useOutletContext<CollectionsOutletType>()
  const navigate = useNavigate()

  const [collections, setCollections] = useState<CollectionRow[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [totalCollections, setTotalCollections] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCollection, setSelectedCollection] = useState<CollectionRow | null>(null)
  const [deleteCollectionId, setDeleteCollectionId] = useState<number | null>(null)

  const { permissions } = useContext(SectionContext)
  const { user } = useContext(UserContext)
  const { setFeedback } = useContext(FeedbackContext)

  const columns = useMemo(() => buildCollectionColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(collectionsScreenKey, columns)

  const canDeleteCollection = useCallback((): boolean => {
    return true
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    if (setIsLoading) setIsLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const mappedFilters = appliedFilters && appliedFilters.length > 0
        ? appliedFilters.map(f => ({
            ...f,
            key: f.key === 'collectionName' ? 'name' : f.key
          }))
        : undefined;

      const response = await axios.get(`${FASHION_API_URL}/collections`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          limit: visibleCount,
          offset: 0,
          search: search || undefined,
          filters: mappedFilters ? JSON.stringify(mappedFilters) : undefined,
        },
      })

      let backendData: any[] = []
      if (Array.isArray(response.data)) {
        backendData = response.data
        setTotalCollections(null)
      } else if (response.data && Array.isArray(response.data.content)) {
        backendData = response.data.content
        const total = response.data.totalElements
        setTotalCollections(typeof total === 'number' ? total : null)
      }

      let fetchedCollections: CollectionRow[] = await Promise.all(
        backendData.map(async (item: any) => {
          const gradeNames = (item.GradeInCollection || [])
            .map((g: any) => g.Grade?.name)
            .filter(Boolean)
          const gradesStr = gradeNames.length > 0 ? gradeNames.join(', ') : '-'

          const groupNames = (item.GroupInCollection || [])
            .map((g: any) => g.Group?.name)
            .filter(Boolean)
          const groupsStr = groupNames.length > 0 ? groupNames.join(', ') : '-'

          const episodeNames = (item.Episode || [])
            .map((ep: any) => ep.name)
            .filter(Boolean)
          const episodesStr = episodeNames.length > 0 ? [...new Set(episodeNames)].join(', ') : '-'

          const fabricCodes = (item.CollectionMaterial || [])
            .filter((cm: any) => cm.Stock || cm.stockId != null)
            .map((cm: any) => {
              const stock = cm.Stock
              if (!stock) return null
              const raw = stock.Code?.code || stock.code || stock.codeId || null
              return sanitizeCode(raw) || null
            })
            .filter(Boolean)
          const fabricsStr = fabricCodes.length > 0 ? [...new Set(fabricCodes)].join(', ') : '-'

          const trimmingCodes = (item.CollectionMaterial || [])
            .filter((cm: any) => cm.TrimResource || cm.resourceId != null)
            .map((cm: any) => {
              const resource = cm.TrimResource
              if (!resource) return null
              const raw = resource.Code?.code || resource.code || resource.codeId || null
              return sanitizeCode(raw) || null
            })
            .filter(Boolean)
          const uniqueTrimmings = [...new Set(trimmingCodes)]
          const trimmingsStr = uniqueTrimmings.length > 0 ? uniqueTrimmings.join(', ') : '-'

          const collectionColorNames = (item.CollectionColor || [])
            .map((cc: any) => cc.name || cc.CollectionColor?.name)
            .filter(Boolean)
          const collectionColorStr = collectionColorNames.length > 0 ? [...new Set(collectionColorNames)].join(', ') : '-'

          const familyNames = (item.Family || [])
            .map((f: any) => f.name)
            .filter(Boolean)
          const familiesStr = familyNames.length > 0 ? familyNames.join(', ') : '-'

          return {
            id: item.id,
            collectionName: item.name || 'Sem nome',
            brand: item.brand || item.Brand || '-',
            collectionColor: collectionColorStr,
            families: familiesStr,
            grades: gradesStr,
            groups: groupsStr,
            fabrics: fabricsStr,
            trimmings: trimmingsStr,
            episodes: episodesStr,
          }
        }),
      )

      fetchedCollections.sort((a, b) => b.id - a.id)

      if (search) {
        const searchLower = search.toLowerCase()
        fetchedCollections = fetchedCollections.filter(
          (item) =>
            item.collectionName.toLowerCase().includes(searchLower) ||
            (item.brand || '').toLowerCase().includes(searchLower) ||
            item.collectionColor.toLowerCase().includes(searchLower) ||
            item.families.toLowerCase().includes(searchLower) ||
            item.grades.toLowerCase().includes(searchLower) ||
            item.groups.toLowerCase().includes(searchLower) ||
            item.fabrics.toLowerCase().includes(searchLower) ||
            item.trimmings.toLowerCase().includes(searchLower) ||
            item.episodes.toLowerCase().includes(searchLower),
        )
      }

      setCollections(fetchedCollections)
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    } catch (err: any) {
      console.error('Erro ao buscar coleções:', err)
      setError(
        err.response?.data?.message ||
        'Erro ao carregar coleções. Tente novamente.',
      )
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    }
  }, [search, setIsLoading, visibleCount, appliedFilters])

  useEffect(() => {
    fetchData()
  }, [fetchData, refresh, visibleCount])

  const handleView = (collection: CollectionRow) => {
    setSelectedCollection(collection)
  }

  const handleEdit = (collection: CollectionRow) => {
    navigate(`edit/${collection.id}`)
  }

  const handleDelete = (id: number) => {
    if (!canDeleteCollection()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Você não tem permissão para deletar coleções. Apenas usuários autorizados podem realizar esta ação.',
      })
      return
    }

    setDeleteCollectionId(id)
  }

  const handleConfirmDelete = async () => {
    if (!deleteCollectionId) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${FASHION_API_URL}/collections/${deleteCollectionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      setFeedback({ isOpen: true, statusOk: true, message: 'Coleção excluída com sucesso!' })
      setDeleteCollectionId(null)
      fetchData()
    } catch (err: any) {
      console.error('Erro ao deletar coleção:', err)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: err.response?.data?.message || 'Erro ao deletar coleção. Tente novamente.',
      })
    }
  }

  const handleCancelDelete = () => {
    setDeleteCollectionId(null)
  }



  if (error) {
    return (
      <CollectionsTableContainer>
        <InfoMessage style={{ color: 'red' }}>
          <p>{error}</p>
        </InfoMessage>
      </CollectionsTableContainer>
    )
  }

  return (
    <CollectionsTableContainer>
      <StandardDataTable
        tableName="Collection"
        columns={columns}
        data={collections}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={totalCollections}
        onLoadMore={() => setVisibleCount((prev) => prev + 50)}
        hasMore={typeof totalCollections === 'number' ? collections.length < totalCollections : false}
        isLoading={loading}
        countLabel="coleções"
        exportFileName="colecoes"
        emptyMessage="Nenhuma coleção encontrada."
        rowActions={(collection) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleView(collection) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleEdit(collection) },
          { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleDelete(collection.id) },
        ]}
      />

      {selectedCollection && (
        <ViewCollectionModal
          collectionId={selectedCollection.id}
          onClose={() => {
            setSelectedCollection(null);
            fetchData();
          }}
        />
      )}

      <ConfirmationModal
        isOpen={deleteCollectionId !== null}
        title='Confirmar exclusão'
        message='Tem certeza que deseja excluir esta coleção? Esta ação não pode ser desfeita.'
        actions={[
          { label: 'Cancelar', onClick: handleCancelDelete, variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
        ]}
      />
    </CollectionsTableContainer>
  )
}

export default CollectionsTable
